import { create } from 'zustand';
import type {
  Organization,
  OrganizationMember,
  Invitation,
  Category,
  Entity,
  Account,
  UserRole,
  CreateCategoryInput,
  UpdateCategoryInput,
  CreateEntityInput,
  UpdateEntityInput,
  CreateAccountInput,
  UpdateAccountInput,
  ItemType,
} from '@perfin/shared';
import {
  // Organization
  getOrganization,
  setupUserOrganization,
  subscribeOrganizationMembers,
  updateMemberRole,
  removeMember as removeOrgMember,
  // Categories
  subscribeCategories,
  createCategory,
  updateCategory,
  deleteCategory,
  reorderCategories,
  addSubCategory,
  updateSubCategory,
  deleteSubCategory,
  initializeDefaultCategories,
  ensureDefaultCategories,
  filterCategoriesByType,
  // Entities
  subscribeEntities,
  createEntity,
  updateEntity,
  deleteEntity,
  toggleEntityActive,
  // Accounts
  subscribeAccounts,
  createAccount,
  updateAccount,
  deleteAccount,
  setDefaultAccount,
  recordAccountUsage,
  toggleAccountActive,
  // Invitations
  subscribePendingInvitations,
  createInvitation,
  revokeInvitation,
  resendInvitation,
  // Defaults
  DEFAULT_CATEGORIES,
} from '@perfin/shared';

// ============================================================================
// STORE INTERFACE
// ============================================================================

interface SettingsState {
  // State
  organization: Organization | null;
  members: OrganizationMember[];
  invitations: Invitation[];
  categories: Category[];
  entities: Entity[];
  accounts: Account[];
  currentUserId: string | null;
  currentUserRole: UserRole | null;
  loading: boolean;
  error: string | null;
  initialized: string | null; // orgId that was initialized, null if not yet

  // Computed
  isAdmin: boolean;

  // Initialization
  initialize: (orgId: string, userId: string, role: UserRole) => () => void;
  setupNewUserOrganization: (userId: string, displayName?: string) => Promise<string>;

  // Category actions
  addCategory: (input: CreateCategoryInput) => Promise<string>;
  editCategory: (categoryId: string, input: UpdateCategoryInput) => Promise<void>;
  removeCategory: (categoryId: string) => Promise<void>;
  reorderCategoryList: (orderedIds: string[]) => Promise<void>;
  addSubCat: (categoryId: string, name: string) => Promise<string>;
  editSubCat: (categoryId: string, subCategoryId: string, name: string) => Promise<void>;
  removeSubCat: (categoryId: string, subCategoryId: string) => Promise<void>;

  // Entity actions
  addEntity: (input: CreateEntityInput) => Promise<string>;
  editEntity: (entityId: string, input: UpdateEntityInput) => Promise<void>;
  removeEntity: (entityId: string) => Promise<void>;
  toggleEntityStatus: (entityId: string, isActive: boolean) => Promise<void>;

  // Account actions
  addAccount: (input: CreateAccountInput) => Promise<string>;
  editAccount: (accountId: string, input: UpdateAccountInput) => Promise<void>;
  removeAccount: (accountId: string) => Promise<void>;
  setDefaultAcc: (accountId: string) => Promise<void>;
  toggleAccountStatus: (accountId: string, isActive: boolean) => Promise<void>;
  recordUsage: (accountId: string) => Promise<void>;

  // User management (admin only)
  inviteUser: (email: string, role: UserRole) => Promise<string>;
  changeUserRole: (userId: string, role: UserRole) => Promise<void>;
  removeMember: (userId: string) => Promise<void>;
  cancelInvitation: (inviteId: string) => Promise<void>;
  resendInvite: (inviteId: string) => Promise<string>;

  // Selectors
  getCategoriesForType: (itemType: ItemType) => Category[];
  getActiveEntities: () => Entity[];
  getActiveAccounts: () => Account[];
  getAccountsForEntity: (entityId: string) => Account[];
  getSuggestedAccounts: () => Account[];

  // Utilities
  clearError: () => void;
}

// ============================================================================
// STORE IMPLEMENTATION
// ============================================================================

export const useSettingsStore = create<SettingsState>((set, get) => ({
  // Initial state
  organization: null,
  members: [],
  invitations: [],
  categories: [],
  entities: [],
  accounts: [],
  currentUserId: null,
  currentUserRole: null,
  loading: false,
  error: null,
  initialized: null,

  // Computed - superAdmin also has admin privileges
  get isAdmin() {
    const role = get().currentUserRole;
    return role === 'admin' || role === 'superAdmin';
  },

  // ========== INITIALIZATION ==========

  initialize: (orgId: string, userId: string, role: UserRole) => {
    // Prevent double-initialization for the same org
    if (get().initialized === orgId) {
      return () => {};
    }
    set({ loading: true, currentUserId: userId, currentUserRole: role, initialized: orgId });

    // Fetch organization
    getOrganization(orgId).then((org) => {
      set({ organization: org });
    });

    // Subscribe to members
    const unsubMembers = subscribeOrganizationMembers(orgId, (members) => {
      set({ members });
    });

    // Subscribe to categories + ensure defaults exist (additive only, runs once)
    let ensuredDefaults = false;
    const unsubCategories = subscribeCategories(orgId, (categories) => {
      set({ categories, loading: false });
      if (!ensuredDefaults) {
        ensuredDefaults = true;
        ensureDefaultCategories(orgId, DEFAULT_CATEGORIES).catch((err) =>
          console.error('[Settings] Failed to ensure default categories:', err)
        );
      }
    });

    // Subscribe to entities
    const unsubEntities = subscribeEntities(orgId, (entities) => {
      set({ entities });
    });

    // Subscribe to accounts
    const unsubAccounts = subscribeAccounts(orgId, (accounts) => {
      set({ accounts });
    });

    // Subscribe to pending invitations
    const unsubInvitations = subscribePendingInvitations(orgId, (invitations) => {
      set({ invitations });
    });

    // Return cleanup function
    return () => {
      unsubMembers();
      unsubCategories();
      unsubEntities();
      unsubAccounts();
      unsubInvitations();
    };
  },

  setupNewUserOrganization: async (userId: string, displayName?: string) => {
    set({ loading: true, error: null });
    try {
      const orgId = await setupUserOrganization(userId, displayName);

      // Initialize default categories
      await initializeDefaultCategories(orgId, DEFAULT_CATEGORIES);

      set({ loading: false });
      return orgId;
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to setup organization';
      set({ error: errorMessage, loading: false });
      throw error;
    }
  },

  // ========== CATEGORY ACTIONS ==========

  addCategory: async (input: CreateCategoryInput) => {
    const { organization } = get();
    if (!organization) throw new Error('No organization');

    set({ loading: true, error: null });
    try {
      const id = await createCategory(organization.id, input);
      set({ loading: false });
      return id;
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create category';
      set({ error: errorMessage, loading: false });
      throw error;
    }
  },

  editCategory: async (categoryId: string, input: UpdateCategoryInput) => {
    const { organization } = get();
    if (!organization) throw new Error('No organization');

    try {
      await updateCategory(organization.id, categoryId, input);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update category';
      set({ error: errorMessage });
      throw error;
    }
  },

  removeCategory: async (categoryId: string) => {
    const { organization } = get();
    if (!organization) throw new Error('No organization');

    try {
      await deleteCategory(organization.id, categoryId);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete category';
      set({ error: errorMessage });
      throw error;
    }
  },

  reorderCategoryList: async (orderedIds: string[]) => {
    const { organization } = get();
    if (!organization) throw new Error('No organization');

    try {
      await reorderCategories(organization.id, orderedIds);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to reorder categories';
      set({ error: errorMessage });
      throw error;
    }
  },

  addSubCat: async (categoryId: string, name: string) => {
    const { organization } = get();
    if (!organization) throw new Error('No organization');

    try {
      return await addSubCategory(organization.id, categoryId, name);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to add sub-category';
      set({ error: errorMessage });
      throw error;
    }
  },

  editSubCat: async (categoryId: string, subCategoryId: string, name: string) => {
    const { organization } = get();
    if (!organization) throw new Error('No organization');

    try {
      await updateSubCategory(organization.id, categoryId, subCategoryId, name);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update sub-category';
      set({ error: errorMessage });
      throw error;
    }
  },

  removeSubCat: async (categoryId: string, subCategoryId: string) => {
    const { organization } = get();
    if (!organization) throw new Error('No organization');

    try {
      await deleteSubCategory(organization.id, categoryId, subCategoryId);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete sub-category';
      set({ error: errorMessage });
      throw error;
    }
  },

  // ========== ENTITY ACTIONS ==========

  addEntity: async (input: CreateEntityInput) => {
    const { organization } = get();
    if (!organization) throw new Error('No organization');

    set({ loading: true, error: null });
    try {
      const id = await createEntity(organization.id, input);
      set({ loading: false });
      return id;
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create entity';
      set({ error: errorMessage, loading: false });
      throw error;
    }
  },

  editEntity: async (entityId: string, input: UpdateEntityInput) => {
    const { organization } = get();
    if (!organization) throw new Error('No organization');

    try {
      await updateEntity(organization.id, entityId, input);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update entity';
      set({ error: errorMessage });
      throw error;
    }
  },

  removeEntity: async (entityId: string) => {
    const { organization } = get();
    if (!organization) throw new Error('No organization');

    try {
      await deleteEntity(organization.id, entityId);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete entity';
      set({ error: errorMessage });
      throw error;
    }
  },

  toggleEntityStatus: async (entityId: string, isActive: boolean) => {
    const { organization } = get();
    if (!organization) throw new Error('No organization');

    try {
      await toggleEntityActive(organization.id, entityId, isActive);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to toggle entity status';
      set({ error: errorMessage });
      throw error;
    }
  },

  // ========== ACCOUNT ACTIONS ==========

  addAccount: async (input: CreateAccountInput) => {
    const { organization } = get();
    if (!organization) throw new Error('No organization');

    set({ loading: true, error: null });
    try {
      const id = await createAccount(organization.id, input);
      set({ loading: false });
      return id;
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create account';
      set({ error: errorMessage, loading: false });
      throw error;
    }
  },

  editAccount: async (accountId: string, input: UpdateAccountInput) => {
    const { organization } = get();
    if (!organization) throw new Error('No organization');

    try {
      await updateAccount(organization.id, accountId, input);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update account';
      set({ error: errorMessage });
      throw error;
    }
  },

  removeAccount: async (accountId: string) => {
    const { organization } = get();
    if (!organization) throw new Error('No organization');

    try {
      await deleteAccount(organization.id, accountId);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete account';
      set({ error: errorMessage });
      throw error;
    }
  },

  setDefaultAcc: async (accountId: string) => {
    const { organization } = get();
    if (!organization) throw new Error('No organization');

    try {
      await setDefaultAccount(organization.id, accountId);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to set default account';
      set({ error: errorMessage });
      throw error;
    }
  },

  toggleAccountStatus: async (accountId: string, isActive: boolean) => {
    const { organization } = get();
    if (!organization) throw new Error('No organization');

    try {
      await toggleAccountActive(organization.id, accountId, isActive);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to toggle account status';
      set({ error: errorMessage });
      throw error;
    }
  },

  recordUsage: async (accountId: string) => {
    const { organization } = get();
    if (!organization) return;

    try {
      await recordAccountUsage(organization.id, accountId);
    } catch {
      // Silent fail - not critical
    }
  },

  // ========== USER MANAGEMENT (Admin only) ==========

  inviteUser: async (email: string, role: UserRole) => {
    const { organization, currentUserId, members } = get();
    if (!organization || !currentUserId) throw new Error('No organization');

    const currentMember = members.find((m) => m.userId === currentUserId);

    try {
      const token = await createInvitation(
        organization.id,
        email,
        role,
        currentUserId,
        currentMember?.displayName
      );
      return token;
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to send invitation';
      set({ error: errorMessage });
      throw error;
    }
  },

  changeUserRole: async (userId: string, role: UserRole) => {
    const { organization } = get();
    if (!organization) throw new Error('No organization');

    try {
      await updateMemberRole(organization.id, userId, role);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to change user role';
      set({ error: errorMessage });
      throw error;
    }
  },

  removeMember: async (userId: string) => {
    const { organization } = get();
    if (!organization) throw new Error('No organization');

    try {
      await removeOrgMember(organization.id, userId);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to remove member';
      set({ error: errorMessage });
      throw error;
    }
  },

  cancelInvitation: async (inviteId: string) => {
    const { organization } = get();
    if (!organization) throw new Error('No organization');

    try {
      await revokeInvitation(organization.id, inviteId);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to cancel invitation';
      set({ error: errorMessage });
      throw error;
    }
  },

  resendInvite: async (inviteId: string) => {
    const { organization } = get();
    if (!organization) throw new Error('No organization');

    try {
      return await resendInvitation(organization.id, inviteId);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to resend invitation';
      set({ error: errorMessage });
      throw error;
    }
  },

  // ========== SELECTORS ==========

  getCategoriesForType: (itemType: ItemType) => {
    const { categories } = get();
    return filterCategoriesByType(categories, itemType);
  },

  getActiveEntities: () => {
    const { entities } = get();
    return entities.filter((ent) => ent.isActive);
  },

  getActiveAccounts: () => {
    const { accounts } = get();
    return accounts.filter((acc) => acc.isActive);
  },

  getAccountsForEntity: (entityId: string) => {
    const { accounts } = get();
    return accounts.filter((acc) => acc.isActive && acc.entityId === entityId);
  },

  getSuggestedAccounts: () => {
    const { accounts } = get();
    const activeAccounts = accounts.filter((acc) => acc.isActive);

    // Sort by: default first, then lastUsed, then accountName
    return activeAccounts.sort((a, b) => {
      if (a.isDefault && !b.isDefault) return -1;
      if (!a.isDefault && b.isDefault) return 1;

      if (a.lastUsed && b.lastUsed) {
        return b.lastUsed.toMillis() - a.lastUsed.toMillis();
      }
      if (a.lastUsed && !b.lastUsed) return -1;
      if (!a.lastUsed && b.lastUsed) return 1;

      return a.accountName.localeCompare(b.accountName);
    });
  },

  // ========== UTILITIES ==========

  clearError: () => set({ error: null }),
}));

// ============================================================================
// PERMISSION HELPERS
// ============================================================================

/**
 * Check if current user can edit an item
 */
export function canEditItem(
  item: { owner?: string; createdBy: string },
  currentUserId: string,
  isAdmin: boolean
): boolean {
  // Admins can edit all items in the org
  if (isAdmin) {
    return true;
  }

  // Standard users can edit items they own or created
  return item.owner === currentUserId || item.createdBy === currentUserId;
}

/**
 * Check if current user can see an item
 * Note: Visibility is enforced by subscription queries, not this function
 */
export function canViewItem(
  _item: { createdBy: string },
  _currentUserId: string,
  _isAdmin: boolean
): boolean {
  // All items that make it through the subscription query are viewable
  return true;
}
